import { useCustomersContext } from '@src/hooks/useCustomer';
import type { CustomerTradeUpdate } from '@src/providers/CustomersContext.types';
import {
  MixpanelEvent,
  NotificationVariants,
  abbreviateId,
  bpsToPercent,
  formattedDateForSubscription,
  useGlobalToasts,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback, useEffect, useState } from 'react';
import type { CustomerTradeForm } from './types';

interface UseCustomerTradeSubmissionOptions {
  form: CustomerTradeForm;
  isEdit: boolean;
  isBlocked: boolean;
  isOpen: boolean;
  quoteCurrency: string | undefined;
  requestCurrency: string | undefined;
  onSuccess: () => void;
}

export function useCustomerTradeSubmission({
  form,
  isEdit,
  isBlocked,
  isOpen,
  quoteCurrency,
  requestCurrency,
  onSuccess,
}: UseCustomerTradeSubmissionOptions) {
  const customerService = useCustomersContext();
  const mixpanel = useMixpanel();
  const { add: addGlobalToast } = useGlobalToasts();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const submit = useCallback(async () => {
    if (isBlocked) {
      return;
    }
    if (isSubmitting) {
      return;
    }
    if (quoteCurrency == null) {
      return;
    }

    const tradeRequest = buildCustomerTradeRequest({ form, quoteCurrency, requestCurrency });

    setIsSubmitting(true);
    try {
      const response = isEdit
        ? await customerService.updateCustomerTrade(tradeRequest)
        : await customerService.createCustomerTrade(tradeRequest);
      mixpanel.track(isEdit ? MixpanelEvent.ModifyCustomerTrade : MixpanelEvent.CreateCustomerTrade);
      addGlobalToast({
        text: `Customer Trade ${abbreviateId(response.data[0].TradeID)} ${isEdit ? 'updated' : 'created'}.`,
        variant: NotificationVariants.Positive,
      });
      onSuccess();
    } catch (error) {
      addGlobalToast({
        text: error instanceof Error ? error.message : String(error),
        variant: NotificationVariants.Negative,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    addGlobalToast,
    customerService,
    form,
    isBlocked,
    isEdit,
    isSubmitting,
    mixpanel,
    onSuccess,
    quoteCurrency,
    requestCurrency,
  ]);

  return { submit, isSubmitting };
}

export function buildCustomerTradeRequest({
  form,
  quoteCurrency,
  requestCurrency,
}: {
  form: CustomerTradeForm;
  quoteCurrency: string;
  requestCurrency: string | undefined;
}): CustomerTradeUpdate {
  const tradeRequest: CustomerTradeUpdate = {
    Counterparty: form.counterparty,
    Symbol: form.symbol,
    Side: form.side,
    ...(requestCurrency != null && { Currency: requestCurrency }),
    Quantity: form.quantity,
    Price: form.price,
    Amount: form.amount,
    AmountCurrency: quoteCurrency,
    SalesCommission: bpsToPercent(form.salesCommission),
    ClTradeID: form.clTradeID,
    TradeID: form.tradeID,
    MarketAccount: form.marketAccount,
    Comments: form.comments,
    RiskSubAccount: form.riskSubAccount,
    Fee: form.fee,
    ...(form.transactTime != null && { TransactTime: formattedDateForSubscription(form.transactTime) }),
  };

  if (tradeRequest.Fee) {
    tradeRequest.FeeCurrency = form.feeCurrency;
  }

  return tradeRequest;
}
